"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import type { UserContext } from "@/lib/permissions";

export type StaffSection =
    | "overview"
    | "assignments"
    | "investment_challenge"
    | "staff_directory"
    | "activities"
    | "events"
    | "volunteers"
    | "volunteers_interested"
    | "analytics"
    | "admissions"
    | "admissions_review"
    | "platform_insights"
    | "directory"
    | "feedback"
    | "email_auto"
    | "policy_console"
    | "prompts_console"
    | "org_groups"
    | "org_volunteers"
    | "candidate_hub"
    | "geo_settings"
    | "discord"
    | "winback"
    | "candidate_campaign"
    | "governance_projects"
    | "ninety_ops";

interface NavItem {
    id: StaffSection;
    label: string;
    icon: string;
    description: string;
    href?: string;
    adminOnly?: boolean;
}

const CORE_NAV_ITEMS: NavItem[] = [
    { id: "overview",     label: "Home",                 icon: "home",            description: "Personal profile & overview" },
    { id: "candidate_hub", label: "CandidateHub",        icon: "school",          description: "Exam cycle oversight", href: "/candidate-hub" },
    { id: "email_auto",   label: "Email Auto",           icon: "mail",            description: "Automated drafts" },
    { id: "events",       label: "Events",               icon: "event",           description: "Pipeline & kanban" },
    { id: "activities",   label: "Gigs",                 icon: "bolt",            description: "Short-term volunteer tasks" },
    { id: "assignments",  label: "Group Assignments",    icon: "assignment_ind",  description: "Groups & roles" },
    { id: "investment_challenge", label: "Investment Challenge", icon: "emoji_events", description: "Fall 2026 challenge & mailer" },
    { id: "policy_console", label: "Policy Console",     icon: "gavel",           description: "Assign stewards & ratify policy" },
    { id: "staff_directory", label: "Staff Directory",   icon: "folder_shared",   description: "Full staff directory" },
    { id: "directory",    label: "Volunteer Directory",  icon: "analytics",       description: "Member search & analytics" },
    { id: "volunteers",   label: "Volunteers",           icon: "people",          description: "Group health" },
    { id: "volunteers_interested", label: "Volunteers Interested", icon: "volunteer_activism", description: "Interest submissions & volunteer pipeline" },
];

// Staff phoning lists: who to call, and a log of how each call went.
const CAMPAIGN_NAV_ITEMS: NavItem[] = [
    { id: "winback",      label: "Member Win-back",      icon: "person_search",   description: "Lapsed member outreach" },
    { id: "candidate_campaign", label: "Candidate Outreach", icon: "support_agent", description: "Unregistered & unscheduled candidates" },
];

const SECONDARY_NAV_ITEMS: NavItem[] = [
    { id: "ninety_ops",   label: "C&ME Data Points", icon: "monitoring", description: "C&ME data dashboard → Ninety" },
    { id: "admissions",   label: "Admissions",           icon: "how_to_reg",      description: "Membership pipeline" },
    { id: "admissions_review", label: "Admissions Review", icon: "how_to_vote",    description: "Committee & board queues (read only)" },
    { id: "governance_projects", label: "Governance Projects", icon: "account_tree", description: "Regular Meetings prototype" },
    { id: "analytics",    label: "Analytics",            icon: "bar_chart",       description: "Coverage stats" },
    { id: "feedback",     label: "Feedback",             icon: "feedback",        description: "User feedback & bugs" },
    { id: "discord",      label: "Discord",              icon: "forum",           description: "Account link coverage" },
    { id: "geo_settings",  label: "Geo Settings",         icon: "tune",            description: "Pipeline configuration", adminOnly: true },
    { id: "platform_insights", label: "Platform Insights", icon: "insights",     description: "Granular usage & audit" },
    { id: "prompts_console", label: "Prompts Console",   icon: "smart_toy",       description: "LLM prompt registry" },
];

interface Props {
    active: StaffSection;
    onSelect: (s: StaffSection) => void;
    user: UserContext;
    onLogout: () => void;
    mobileOpen?: boolean;
    onMobileClose?: () => void;
}

export function StaffSidebar({ active, onSelect, user, onLogout, mobileOpen, onMobileClose }: Props) {
    const initials = `${user.first_name?.[0] || ""}${user.last_name?.[0] || ""}`.toUpperCase();
    const [isCollapsedRaw, setIsCollapsedRaw] = useState(false);
    const [isDesktop, setIsDesktop] = useState(true);
    // Collapse (icon-only mode) only applies on desktop. On mobile the drawer is
    // always the full 260px panel with labels, regardless of the persisted setting.
    const isCollapsed = isCollapsedRaw && isDesktop;

    // Track desktop breakpoint (matches Tailwind `lg`)
    useEffect(() => {
        const mq = window.matchMedia("(min-width: 1024px)");
        const update = () => setIsDesktop(mq.matches);
        update();
        mq.addEventListener("change", update);
        return () => mq.removeEventListener("change", update);
    }, []);

    // Load collapsed state from localStorage on mount
    useEffect(() => {
        const val = localStorage.getItem("cmta_staff_sidebar_collapsed");
        if (val === "true") {
            setIsCollapsedRaw(true);
        }
    }, []);

    const toggleCollapse = () => {
        const newVal = !isCollapsedRaw;
        setIsCollapsedRaw(newVal);
        localStorage.setItem("cmta_staff_sidebar_collapsed", String(newVal));
    };

    const handleSelect = (s: StaffSection) => {
        onSelect(s);
        onMobileClose?.();
    };

    const filterItem = (item: NavItem) => {
        const userRole = (user.role || "").toLowerCase();
        if (item.id === "email_auto") {
            const email = (user.email || "").toLowerCase();
            const allowedEmails = ["shanaia.alvares@cmtassociation.org", "joel@cmtassociation.org"];
            return userRole === "superadmin" || allowedEmails.includes(email);
        }
        if (item.adminOnly) {
            return userRole === "admin" || userRole === "superadmin" || userRole === "staff" || user.is_staff === true;
        }
        return true;
    };

    const visibleCore = CORE_NAV_ITEMS.filter(filterItem);
    const visibleCampaigns = CAMPAIGN_NAV_ITEMS.filter(filterItem);
    const visibleSecondary = SECONDARY_NAV_ITEMS.filter(filterItem);

    const renderNavItem = (item: NavItem) => {
        const isActive = active === item.id;
        return (
            <Link
                key={item.id}
                href={item.href || `/staff?section=${item.id}`}
                onClick={(e) => {
                    if (item.href) return; // Allow normal navigation for external routes
                    if (e.button === 0 && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
                        e.preventDefault();
                        handleSelect(item.id);
                    }
                }}
                className={`flex items-center transition-all group relative no-underline ${
                    isCollapsed 
                        ? "w-11 h-11 mx-auto rounded-xl justify-center" 
                        : "w-full flex-row gap-3 px-3 py-2.5 rounded-xl"
                } ${
                    isActive
                        ? "bg-white/15 border border-white/20 shadow-inner"
                        : "hover:bg-white/8 border border-transparent"
                }`}
            >
                <div className={`flex items-center justify-center flex-shrink-0 transition-colors ${
                    isCollapsed ? "w-8 h-8 rounded-lg" : "w-8 h-8 rounded-lg"
                } ${
                    !isCollapsed && isActive ? "bg-white/20" : !isCollapsed ? "bg-white/5 group-hover:bg-white/10" : ""
                } relative`}>
                    <span className="material-symbols-outlined text-[18px]">{item.icon}</span>
                </div>
                <div className={`min-w-0 transition-all duration-300 ${isCollapsed ? "w-0 opacity-0 overflow-hidden pointer-events-none flex-shrink-0" : "flex-1 w-auto opacity-100 ml-3 text-left"}`}>
                    <p className={`text-sm font-bold leading-tight whitespace-nowrap ${isActive ? "text-white" : "text-white/70 group-hover:text-white"} transition-colors`}>
                        {item.label}
                    </p>
                    <p className={`text-[10px] leading-tight mt-0.5 whitespace-nowrap ${isActive ? "text-white/60" : "text-white/35 group-hover:text-white/50"} transition-colors`}>
                        {item.description}
                    </p>
                </div>
                {isActive && !isCollapsed && (
                    <div className="ml-auto w-1.5 h-1.5 rounded-full bg-[#7ad4e5] flex-shrink-0" />
                )}

                {/* Premium custom tooltip for collapsed view */}
                {isCollapsed && (
                    <span className="absolute left-[58px] bg-[#1e293b] text-white text-[10px] font-bold px-2.5 py-1.5 rounded-lg border border-slate-700 shadow-xl opacity-0 group-hover:opacity-100 transition-all duration-150 pointer-events-none whitespace-nowrap z-[999] translate-x-[-5px] group-hover:translate-x-0">
                        {item.label}
                    </span>
                )}
            </Link>
        );
    };

    return (
        <>
            {/* Mobile overlay */}
            {mobileOpen && (
                <div
                    className="fixed inset-0 bg-black/40 z-[55] lg:hidden"
                    onClick={onMobileClose}
                />
            )}

            <aside
                style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
                className={`
                    ${isCollapsed ? "lg:w-[68px]" : "lg:w-[260px]"} w-[260px] flex-shrink-0 h-screen bg-[#003d45] text-white flex flex-col
                    fixed lg:sticky top-0 left-0 z-[60] lg:translate-x-0 transition-transform duration-300 ease-in-out overflow-visible
                    ${mobileOpen ? "translate-x-0" : "-translate-x-full"}
                `}
            >
            {/* Brand Header with Integrated Toggle */}
            <div className={`py-5 border-b border-white/10 flex flex-col items-center justify-center relative ${isCollapsed ? "px-0" : "px-6"}`}>
                {!isCollapsed ? (
                    <div className="w-full flex items-center justify-between gap-3">
                        <Link href="/" className="flex items-center gap-3 group no-underline min-w-0">
                            <div className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center group-hover:bg-white/20 transition-colors flex-shrink-0">
                                <span className="material-symbols-outlined text-white text-xl">shield</span>
                            </div>
                            <div className="min-w-0">
                                <p className="text-xs font-black uppercase tracking-[0.18em] text-white/90 leading-tight whitespace-nowrap">Staff Portal</p>
                                <p className="text-[10px] text-white/40 font-medium mt-0.5 whitespace-nowrap">CMT Association</p>
                            </div>
                        </Link>
                        <button
                            onClick={toggleCollapse}
                            className="hidden lg:flex w-7 h-7 rounded-lg bg-white/5 hover:bg-white/15 text-white/70 hover:text-white items-center justify-center transition-colors flex-shrink-0 group relative"
                            title="Collapse sidebar"
                            >
                            <span className="material-symbols-outlined text-base">menu_open</span>
                            <span className="absolute left-[32px] bg-[#1e293b] text-white text-[10px] font-bold px-2.5 py-1.5 rounded-lg border border-slate-700 shadow-xl opacity-0 group-hover:opacity-100 transition-all duration-150 pointer-events-none whitespace-nowrap z-[999] translate-x-[-5px] group-hover:translate-x-0">
                                Collapse Sidebar
                            </span>
                        </button>
                    </div>
                ) : (
                    <button
                        onClick={toggleCollapse}
                        className="w-10 h-10 rounded-xl bg-white/5 hover:bg-white/15 text-white flex items-center justify-center transition-colors group relative"
                        title="Expand sidebar"
                    >
                        <span className="material-symbols-outlined text-xl">menu</span>
                        <span className="absolute left-[58px] bg-[#1e293b] text-white text-[10px] font-bold px-2.5 py-1.5 rounded-lg border border-slate-700 shadow-xl opacity-0 group-hover:opacity-100 transition-all duration-150 pointer-events-none whitespace-nowrap z-[999] translate-x-[-5px] group-hover:translate-x-0">
                            Expand Sidebar
                        </span>
                    </button>
                )}
            </div>

            {/* Nav */}
            <nav id="staff-sidebar-nav" className={`flex-1 py-3 space-y-1 overflow-y-auto [&::-webkit-scrollbar]:hidden ${isCollapsed ? "px-2" : "px-3"}`}>
                <p className={`text-[9px] font-black uppercase tracking-[0.22em] text-white/30 pt-1 pb-2 transition-all duration-300 ${isCollapsed ? "text-center px-0 overflow-hidden text-[0px] h-0 opacity-0" : "px-3"}`}>
                    Core Operations
                </p>
                {visibleCore.map(renderNavItem)}

                {visibleCampaigns.length > 0 && (
                    <>
                        <div className={`pt-3 pb-1 transition-all duration-300 ${isCollapsed ? "border-t border-white/10 my-2 pt-0 pb-0" : ""}`}>
                            <p className={`text-[9px] font-black uppercase tracking-[0.22em] text-white/30 pb-2 transition-all duration-300 ${isCollapsed ? "text-center px-0 overflow-hidden text-[0px] h-0 opacity-0" : "px-3 border-t border-white/10 pt-3"}`}>
                                Calling Campaigns
                            </p>
                        </div>
                        {visibleCampaigns.map(renderNavItem)}
                    </>
                )}

                {visibleSecondary.length > 0 && (
                    <>
                        <div className={`pt-3 pb-1 transition-all duration-300 ${isCollapsed ? "border-t border-white/10 my-2 pt-0 pb-0" : ""}`}>
                            <p className={`text-[9px] font-black uppercase tracking-[0.22em] text-white/30 pb-2 transition-all duration-300 ${isCollapsed ? "text-center px-0 overflow-hidden text-[0px] h-0 opacity-0" : "px-3 border-t border-white/10 pt-3"}`}>
                                Management & Insights
                            </p>
                        </div>
                        {visibleSecondary.map(renderNavItem)}
                    </>
                )}
            </nav>

            {/* Footer — user info */}
            <div className={`py-4 border-t border-white/10 mt-auto ${isCollapsed ? "px-2 text-center space-y-4" : "px-4 space-y-3"}`}>
                <Link href="/dashboard" className={`flex items-center gap-2 text-[11px] text-white/50 hover:text-white/80 transition-colors ${isCollapsed ? "justify-center" : ""}`}>
                    <span className="material-symbols-outlined text-sm">arrow_back</span>
                    <span className={`transition-all duration-300 ${isCollapsed ? "w-0 opacity-0 overflow-hidden pointer-events-none hidden" : "w-auto opacity-100"}`}>Back to dashboard</span>
                </Link>
                <div className={`flex items-center ${isCollapsed ? "flex-col gap-3 justify-center" : "gap-3"}`}>
                    {user.profile_photo_url ? (
                        <img src={user.profile_photo_url} alt="avatar" className="w-9 h-9 rounded-full object-cover border border-white/20 flex-shrink-0" />
                    ) : (
                        <div className="w-9 h-9 rounded-full bg-white/15 border border-white/20 flex items-center justify-center text-xs font-black flex-shrink-0">
                            {initials}
                        </div>
                    )}
                    <div className={`min-w-0 flex-1 text-left transition-all duration-300 ${isCollapsed ? "w-0 opacity-0 overflow-hidden pointer-events-none hidden" : "w-auto opacity-100 block"}`}>
                        <p className="text-sm font-bold text-white truncate">{user.first_name} {user.last_name}</p>
                        <p className="text-[10px] text-white/40 uppercase tracking-widest font-black">{user.role}</p>
                    </div>
                    <button
                        onClick={onLogout}
                        title="Sign out"
                        className={`text-white/30 hover:text-white transition-colors flex-shrink-0 ${isCollapsed ? "p-2 hover:bg-white/10 rounded-lg" : ""}`}
                    >
                        <span className="material-symbols-outlined text-base">logout</span>
                    </button>
                </div>
            </div>
        </aside>
        </>
    );
}
